import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Buildings, Trash, Warning } from "@phosphor-icons/react";

export default function Settings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/contracts/settings")).data });
  const [form, setForm] = useState({});

  useEffect(() => { if (data) setForm(data); }, [data]);

  const save = useMutation({
    mutationFn: async (payload) => (await api.patch("/contracts/settings", payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); toast.success("Settings saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const wipe = useMutation({
    mutationFn: async () => (await api.post("/admin/wipe-test-data")).data,
    onSuccess: (r) => {
      const counts = r?.deleted || {};
      const parts = Object.entries(counts).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(", ");
      qc.invalidateQueries();
      toast.success(parts ? `Wiped: ${parts}` : "No test records matched — nothing deleted.");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Wipe failed"),
  });

  if (isLoading) return <div className="p-10 text-slate-400">Loading…</div>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setNum = (k) => (e) => setForm({ ...form, [k]: parseFloat(e.target.value || 0) });

  return (
    <div data-testid="settings-page">
      <PageHeader
        eyebrow="Configuration"
        title="Company Settings"
        description="Defaults used in auto-generated contracts, invoices, and emails. Admin only."
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section icon={<Buildings size={16} weight="duotone" />} title="Company">
          <F label="Display name"><Input value={form.company_name || ""} onChange={set("company_name")} data-testid="s-name" /></F>
          <F label="Legal name (for contracts)"><Input value={form.company_legal_name || ""} onChange={set("company_legal_name")} data-testid="s-legal" /></F>
          <F label="Phone"><Input value={form.company_phone || ""} onChange={set("company_phone")} /></F>
          <F label="Email"><Input value={form.company_email || ""} onChange={set("company_email")} /></F>
          <F label="Website"><Input value={form.company_website || ""} onChange={set("company_website")} /></F>
          <F label="State of incorporation / governing law">
            <Input value={form.company_state || ""} onChange={set("company_state")} placeholder="Maryland" />
          </F>
        </Section>

        <Section icon={<Buildings size={16} weight="duotone" />} title="Signing representative">
          <F label="Rep printed name"><Input value={form.rep_name || ""} onChange={set("rep_name")} data-testid="s-rep-name" /></F>
          <F label="Rep title"><Input value={form.rep_title || ""} onChange={set("rep_title")} placeholder="Authorized Representative" /></F>
        </Section>

        <Section icon={<Buildings size={16} weight="duotone" />} title="Interpreter contract defaults">
          <F label="Default hourly rate ($)"><Input type="number" value={form.interpreter_default_hourly ?? ""} onChange={setNum("interpreter_default_hourly")} /></F>
          <F label="Minimum billable hours (in-person)"><Input type="number" value={form.interpreter_min_hours ?? ""} onChange={setNum("interpreter_min_hours")} /></F>
          <F label="Invoice payment window (days)"><Input type="number" value={form.invoice_due_days ?? ""} onChange={setNum("invoice_due_days")} /></F>
        </Section>
      </div>
      <div className="mt-6 flex justify-end">
        <Button onClick={() => save.mutate(form)} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="settings-save">
          Save settings
        </Button>
      </div>

      {/* Danger zone */}
      <div className="mt-10 border border-red-200 bg-red-50/40 rounded-xl p-5" data-testid="danger-zone">
        <div className="flex items-center gap-2 mb-2">
          <Warning size={18} weight="duotone" className="text-red-700" />
          <div className="font-display font-semibold text-red-900">Danger zone</div>
        </div>
        <div className="text-sm text-slate-700 mb-4">
          Wipe every record whose name/email/subject/number starts with <b>TEST_</b>, <b>UI_</b> or
          <b>test.user.</b>, or whose email is <b>delivered@resend.dev</b>. Prefix match is case-sensitive
          and requires the underscore — real names like "UI Design Corp" are safe. Cascades to related
          projects, invoices, communications, contracts and transactions.
        </div>
        <Button
          variant="outline"
          data-testid="wipe-test-data-btn"
          onClick={() => {
            if (window.confirm("Wipe all TEST_/UI_/test.user./delivered@resend.dev records and their cascaded projects, invoices, communications, contracts and transactions? Real records are NOT affected.")) {
              wipe.mutate();
            }
          }}
          disabled={wipe.isPending}
          className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
        >
          <Trash size={14} className="mr-1.5" /> {wipe.isPending ? "Wiping…" : "Wipe test data"}
        </Button>
      </div>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="card-flat p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-blue-800">{icon}</span>
        <div className="font-display font-semibold text-slate-900">{title}</div>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
